import { Button, Dialog, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
	BooleanField,
	BooleanInput,
	CreateButton,
	Datagrid,
	DateField,
	ExportButton,
	FilterButton,
	FunctionField,
	List,
	NumberField,
	TextInput,
	TopToolbar,
	useResourceContext,
} from 'react-admin';
import { BiCalendar, BiLink } from 'react-icons/bi';
import FreeBusyCalendar from '../FreeBusyCalendar/FreeBusyCalendar';
import MyPagination from '../MyPagination';
import { downloadEncodedCSV } from '../../utils/functions';
import moment from 'moment';
import Image from '../UIElements/Image';
import { useLocation } from 'react-router-dom';

const ExpertList = () => {
	const [idForFreeBusy, setIdForFreeBusy] = useState<string | null>(null);
	const [freeBusyDialogTitle, setFreeBusyDialogTitle] = useState('');
	const context = useResourceContext();
	const location = useLocation();
	// is_global is a custom query param from the menu ("global" tab). react-admin
	// rewrites the URL when filters change and drops this param, so we must keep our
	// own state for it and only resync when the URL is a "clean" navigation (menu click),
	// not a filter update.
	const readIsGlobalFromUrl = (): boolean | null => {
		const search = location.search || '';
		const hashQuery =
			typeof window !== 'undefined' ? window.location.hash.split('?')[1] || '' : '';
		const raw = search || hashQuery;
		const params = new URLSearchParams(raw);
		// If react-admin already put its list state in the URL, don't touch the flag —
		// the user is filtering/sorting, not switching tabs.
		const hasRaListState =
			params.has('displayedFilters') ||
			params.has('filter') ||
			params.has('sort') ||
			params.has('page');
		if (hasRaListState) return null;
		return params.get('is_global') === 'true';
	};

	const [isGlobalFromQuery, setIsGlobalFromQuery] = useState<boolean>(() => {
		const initial = readIsGlobalFromUrl();
		return initial ?? false;
	});

	useEffect(() => {
		const next = readIsGlobalFromUrl();
		if (next !== null && next !== isGlobalFromQuery) {
			setIsGlobalFromQuery(next);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, location.search]);
	const isExpertsResource = context === 'instructor/allin/experts';
	const isSafeSpaceResource = context === 'instructor/allin/safe-space';
	const isGlobalResource = isExpertsResource || isSafeSpaceResource || isGlobalFromQuery;
	const listResource = isExpertsResource
		? 'instructor/allin/experts'
		: isSafeSpaceResource
			? 'instructor/allin/safe-space'
			: 'instructor/allin';
	const filters = [
		<TextInput resource={context} source="name" />,
		// GH-728 (Terra): find an expert by email — partial, case-insensitive (the server turns text filters into a regex)
		<TextInput resource={context} source="email" label="מייל" />,
		<BooleanInput resource={context} source="active" />,
		<BooleanInput source="showInSubscription" label="מופיע/ה במנוי" />,
		<BooleanInput source="safe_space" label="מרחב בטוח" />,
		<BooleanInput source="bad_zoom_link" label="קישור זום לא תקין" />,
	];

	const exporter = (experts: any) => {
		const expertsToExport = experts.map((expert: any) => ({
			name: expert.name,
			english_name: expert.english_name,
			active: expert.active,
			safe_space: expert.safe_space,
			google: Boolean(expert.google),
			email: expert.email,
			availableSlotsNextWeek: expert.availableSlotsNextWeek,
		}));

		downloadEncodedCSV(
			expertsToExport,
			['name', 'active', 'safe_space', 'google', 'email', 'availableSlotsNextWeek'],
			`Experts_${moment().format('YYYY_MM_DD')}`
		);
	};
	return (
		<>
			<Dialog open={Boolean(idForFreeBusy)} maxWidth={'xl'} onClose={() => setIdForFreeBusy(null)}>
				<div className="p-4">
					{idForFreeBusy ? <FreeBusyCalendar title={freeBusyDialogTitle} id={idForFreeBusy} /> : null}
				</div>
			</Dialog>
			<List
				resource={listResource}
				filters={filters}
				filter={isGlobalFromQuery ? { is_global: true } : undefined}
				actions={
					<TopToolbar>
						<CreateButton resource="instructor" />
						<FilterButton />
						<ExportButton exporter={exporter} />
					</TopToolbar>
				}
				perPage={50}
				pagination={<MyPagination />}
			>
				<Datagrid
					rowClick={(id) => (isGlobalResource ? `/instructor/${id}` : 'edit')}
					dir="rtl"
					resource={context}
					size="small"
					style={{ tableLayout: 'auto' }}
					bulkActionButtons={false}
					rowStyle={(record: any) => ({
						backgroundColor: (record?.inStep === 1 || record?.inStep === 3 || record?.inStep === 5) ? '#fff3cd' : 'transparent',
					})}
				>
					<BooleanField source="active" label="פעיל/ה" />
					<BooleanField source="showInSubscription" label="מופיע במנוי" />
					<FunctionField
						source="inStep"
						label="בתהליך"
						textAlign="right"
						render={(record: any) => {
							let text = '';
							let color = '';
							
							if (record?.inStep === 0) {
								text = 'הושלם';
								color = '#2e7d32';
							} else if (record?.inStep === -1) {
								text = 'סורב על ידי המערכת';
								color = '#d32f2f';
							} else if (record?.inStep === 1) {
								text = 'מילא טופס ראשון';
								color = '#ed6c02';
							} else if (record?.inStep === 2) {
								text = 'אושר טופס ראשון';
								color = '#ed6c02';
							} else if (record?.inStep === 3) {
								text = 'מילא טופס שני';
								color = '#ed6c02';
							} 
							else if (record?.inStep === 4) {
								text = 'אושר טופס שני';
								color = '#ed6c02';
							} 
							else if (record?.inStep === 5) {
								text = 'סיים הזנה באדמין';
								color = '#ed6c02';
							} 
							else {
								text = record?.inStep ?? '';
								color = '#ed6c02';
							}
							
							return (
								<Typography variant="body2" sx={{ color, fontWeight: 'bold' }} dir="rtl">
									{text}
								</Typography>
							);
						}}
					/>
					{/* GH-744 — join date (experts + therapists), sortable; very old records have no createdAt → empty */}
					<DateField source="createdAt" label="הצטרפות" locales="he-IL" sortable />

					<FunctionField
						width={20}
						sortBy="name"
						label="שם"
						render={(record: any) => {
							return (
								<div className="flex flex-row gap-2 items-center">
									<div
										style={{
											width: 80,
											height: 80,
											alignItems: 'center',
											justifyContent: 'center',
											position: 'relative',
										}}
									>
										<div
											style={{
												backgroundColor: '#FA4D46',
												height: 82,
												width: 82,
												position: 'absolute',
												borderRadius: 100,
												aspectRatio: 1,
												borderWidth: 0,
												top: -3,
												left: 2,
											}}
										/>
										<div
											style={{
												borderRadius: 80,
												width: 80,
												height: 80,
												overflow: 'hidden',
											}}
										>
											<Image src={record.image} anchor={record.anchor} />
										</div>
									</div>
									<div>
										<span className="flex flex-col justify-between h-full">
											<div className="flex flex-row gap-2 items-center">
												<span className="font-bold text-lg">{record.name}</span>
												{Boolean(record.google) ? (
													<Tooltip title="יומן גוגל מסונכרן">
														<span>
															<i className="fab fa-google fa-1x" />
														</span>
													</Tooltip>
												) : null}
											</div>
											<div>
												<span className="text-xs max-w-80">{record.title} </span>
												<div>
													{record.category?.length ? (
														<Tooltip
															title={
																<ul className=" leading-tight list-disc">
																	{record.category.map((category: any) => (
																		<li className="text-xs ">
																			{category.hebrew_name}
																		</li>
																	))}
																</ul>
															}
														>
															<span className="">
																{record.category?.length === 1
																	? 'קטגוריה אחת'
																	: `${record.category?.length} קטגוריות`}
															</span>
														</Tooltip>
													) : null}
												</div>
												{record.safe_space ? (
													<div className="font-bold text-xs">מרחב בטוח</div>
												) : null}
											</div>
										</span>
									</div>
								</div>
							);
						}}
					/>
					<FunctionField
						source="admin_info"
						label="הערות"
						textAlign="right"
						render={(record: any) => record?.admin_info || ''}
					/>
					<NumberField source="priority" />
					<FunctionField
						textAlign="right"
						render={(record: any) => {
							const warnings = [];
							if (!record.email) warnings.push('אין אימייל');
							if (!record.image) warnings.push('אין תמונה');
							if (!record.title) warnings.push('אין טייטל');
							if (!record.english_title) warnings.push('אין טייטל באנגלית');
							if (!record.english_name) warnings.push('אין שם באנגלית');
							if (!record.account_number) warnings.push('אין מספר חשבון');
							if (!record.insuranceExpiryDate) warnings.push('אין תאריך תפוגת ביטוח');
							if (record.bad_zoom_link) {
								if (!record.zoom) warnings.push('אין קישור זום');
								else warnings.push('קישור זום לא תקין');
							}

							return (
								<Typography variant="caption" textAlign={'right'} color="#CA0B00">
									{warnings.join(', ')}
								</Typography>
							);
						}}
					/>
					<FunctionField
						label="קישור לעמוד"
						textAlign="right"
						render={(record: any) => {
							return (
								<Button
									variant="text"
									onClick={(e) => {
										e.stopPropagation();
										navigator.clipboard.writeText(
											`https://app.grouphug.online/expert/${record._id}/`
										);
										return;
									}}
								>
									<BiLink />
								</Button>
							);
						}}
					/>
					<NumberField source="availableSlotsNextWeek" />
					<FunctionField
						label="בדיקת יומן"
						textAlign="right"
						render={(record: any) => {
							return (
								<Button
									variant="text"
									onClick={(e) => {
										e.stopPropagation();
										setIdForFreeBusy(record._id);
										setFreeBusyDialogTitle(record.name);
										return;
									}}
								>
									<BiCalendar />
								</Button>
							);
						}}
					/>
				</Datagrid>
			</List>
		</>
	);
};

export default ExpertList;
